import React, { useState, useEffect } from 'react'
import { Container } from 'pcln-design-system'
import fetch from 'isomorphic-unfetch'
import { OptInBody, OptInText, SignUpComplete } from '@pcln/email-signup'
import {
  useContentful,
  getVariantContent,
  ContentfulRichText,
  IVariationCollectionType,
  IGoogleAnalyticsTrackingType,
  ICallToActionType
} from '@pcln/contentful-components'
import styled from 'styled-components'
import { useFeature } from '@pcln/feature-components'
import analytics from '../../utils/analytics'
import useSeti from '../CustomHooks/useSeti'
import isContentIVariationCollectionType from '../../utils/is-content-ivariation-collection-type'
import useLocalStorageToTrackOptin from './customHooks/useLocalStorageToTrackOptin'
import CONTENT from './content'

// cannot use types from contentful-components because of a breaking change.
// when we update contentful-components these types can be removed
export interface OptInType {
  internalName?: string
  headline?: any
  subheadline?: any
  alertId?: '1 | 23 | 60 | 72 | 73 | 8832'
  channelType?: 'EMAIL | NTFSVCS'
  contactSourceType?: 'CS | ASP | WEB | PM | MOBI | HP'
  callsToAction?: ICallToActionType[] | null
  input?: Input | null
  disclaimer?: any
  successHeadline?: string
  googleAnalyticsTracking?: IGoogleAnalyticsTrackingType | null
}

export interface Input {
  internalName?: string
  id: string
  placeholder: string
  error: string
  inputType?: 'EMAIL | PHONE | FIRST-NAME'
}

type EmailSignupProps = {
  closeToast?: () => void
  setiVariantHasContent?: (arg: boolean) => void
  isToast?: boolean
  sourceId: string
  isToastVisibile?: boolean
}

const getContentTypeId = (isToast: boolean) => {
  return isToast ? 'emailOptInToastModule' : 'emailOptIn'
}

function mapContentfulContent(optIn: any) {
  const { url, label, analyticsTracking } = optIn.callToAction ?? {}
  const { placeholder, error } = optIn.input[0] ?? {}
  const {
    headline,
    subheadline,
    successHeadline,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTracking
  } = optIn
  return {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError: new Error(error),
    buttonText: label,
    url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView: {
      category: googleAnalyticsTracking?.category || 'email opt-in',
      action: googleAnalyticsTracking?.action,
      label: googleAnalyticsTracking?.label
    },
    googleAnalyticsTrackingOnClick: {
      category: analyticsTracking?.category,
      action: analyticsTracking?.action,
      label: analyticsTracking?.label
    }
  }
}

const RichText = styled(ContentfulRichText)`
  margin: 0px;
`

export default function EmailSignUpComponent({
  closeToast,
  isToast = false,
  sourceId,
  isToastVisibile = false,
  setiVariantHasContent
}: EmailSignupProps) {
  const contentfulEnabled = useFeature('siteExperience.contentfulEnabled', true)

  const optInFromContentful = useContentful(getContentTypeId(isToast)) as
    | OptInType
    | IVariationCollectionType
    | undefined

  const isVariantCollection =
    isContentIVariationCollectionType<OptInType>(optInFromContentful)

  const setiName = useSeti(
    isVariantCollection ? optInFromContentful.experimentName : ''
  )

  const optIn = isVariantCollection
    ? (getVariantContent(setiName, optInFromContentful) as
        | OptInType
        | undefined)
    : optInFromContentful

  const contentfulEnabledAndOptIn = contentfulEnabled && optIn != null

  useEffect(() => {
    if (setiVariantHasContent) setiVariantHasContent(contentfulEnabledAndOptIn)
  }, [contentfulEnabledAndOptIn, setiVariantHasContent])

  const {
    headline,
    subheadline,
    successHeadline,
    placeholder,
    invalidEmailError,
    buttonText,
    url,
    alertId,
    channelType,
    contactSourceType,
    googleAnalyticsTrackingOnView,
    googleAnalyticsTrackingOnClick
  } = contentfulEnabledAndOptIn
    ? mapContentfulContent(optIn as OptInType)
    : CONTENT

  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<Error | undefined>(undefined)
  const [emailEntered, setEmailEntered] = useState<string>('')
  const [fireAnalyticsOnce, setFireAnalyticsOnce] = useState(false)
  const { onSuccess } = useLocalStorageToTrackOptin()

  const submitEmail = async ({
    emailAddress
  }: {
    emailAddress: string
  }): Promise<void> => {
    setIsLoading(true)
    setEmailEntered(emailAddress)
    const submission = {
      alertSubscribeList: [
        {
          emailAddress,
          sourceId,
          alertId,
          channelType,
          contactSourceType,
          partnerCode: 'PCLN'
        }
      ]
    }

    try {
      const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify(submission),
        headers: { 'content-type': 'application/json' }
      })

      if (response.ok) {
        const body = await response.json()
        if (body.success) {
          onSuccess()
          setSuccess(true)
          if (googleAnalyticsTrackingOnClick?.action) {
            analytics.fireEvent({
              action: googleAnalyticsTrackingOnClick.action,
              category: googleAnalyticsTrackingOnClick.category,
              label: googleAnalyticsTrackingOnClick.label
            })
          }
        } else {
          throw new Error(body.errorDesc || 'Email registration failure')
        }
      } else {
        throw new Error(response.statusText || 'Email registration failure')
      }
    } catch (err) {
      if (err instanceof Error) {
        setError(err)
      }
    }
    setIsLoading(false)
  }

  useEffect(() => {
    const fireAnalyticsOnView = () => {
      if (googleAnalyticsTrackingOnView?.action) {
        analytics.fireEvent({
          action: googleAnalyticsTrackingOnView.action,
          category: googleAnalyticsTrackingOnView.category,
          label: googleAnalyticsTrackingOnView.label
        })
        setFireAnalyticsOnce(true)
      }
    }

    if (!fireAnalyticsOnce) {
      if (isToast) {
        if (isToastVisibile) fireAnalyticsOnView()
      } else {
        fireAnalyticsOnView()
      }
    }
  }, [
    googleAnalyticsTrackingOnView.action,
    googleAnalyticsTrackingOnView.category,
    googleAnalyticsTrackingOnView.label,
    isToast,
    isToastVisibile,
    fireAnalyticsOnce
  ])
  const isCloseButtonShown = !!closeToast
  return (
    <Container>
      <OptInBody
        success={success}
        apiErrorMessage={error}
        invaildEmailErrorMessage={invalidEmailError}
        isLoading={isLoading}
        submitEmail={email => submitEmail(email)}
        buttonText={buttonText}
        placeholder={placeholder}
        successTemplate={
          <SignUpComplete
            headline={<RichText body={successHeadline} />}
            subheadline={emailEntered}
            closeToast={closeToast}
            isCloseButtonShown={isCloseButtonShown}
          />
        }
      >
        <OptInText
          headline={<RichText body={headline} />}
          subheadline={<RichText body={subheadline} />}
        />
      </OptInBody>
    </Container>
  )
}
